import { Button, Form, Input } from 'antd';
import { LockOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { IPasswordValidation } from 'src/shared/interface/forgot-password';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import clsx from 'clsx';

interface INewPasswordFormProps {
  password: string;
  confirmPassword: string;
  validation: IPasswordValidation;
  isValid: boolean;
  loading: boolean;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onSubmit: () => void;
  onBack: () => void;
}

export const NewPasswordForm = ({
  password,
  confirmPassword,
  validation,
  isValid,
  loading,
  onPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
  onBack,
}: INewPasswordFormProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [form] = Form.useForm();

  const handleFinish = () => {
    if (isValid) {
      onSubmit();
    }
  };

  const ValidationItem = ({ isValid, text }: { isValid: boolean; text: string }) => (
    <div className='flex items-center gap-2 text-sm py-1'>
      {isValid ? (
        <CheckCircleOutlined className='text-green-500' />
      ) : (
        <CloseCircleOutlined className='text-red-500' />
      )}
      <span className={clsx('text-gray-600', { 'text-green-600': isValid })}>{text}</span>
    </div>
  );

  return (
    <div className='w-full max-h-[calc(100vh-200px)] overflow-y-auto'>
      <div className='mb-6 text-center'>
        <div className='mb-2'>
          <span className='inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-3'>
            <LockOutlined className='text-3xl text-blue-600' />
          </span>
        </div>
        <h3 className='text-lg font-semibold mb-2'>Tạo mật khẩu mới</h3>
      </div>

      <Form form={form} layout='vertical' onFinish={handleFinish}>
        <Form.Item
          label='Mật khẩu mới'
          name='password'
          rules={[
            {
              required: true,
              message: 'Vui lòng nhập mật khẩu mới',
            },
          ]}
        >
          <Input
            prefix={<LockOutlined className='text-gray-400' />}
            suffix={
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                className='cursor-pointer text-gray-400 hover:text-gray-600 border-0 bg-transparent p-0'
                aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
              >
                {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </button>
            }
            type={showPassword ? 'text' : 'password'}
            placeholder='Nhập mật khẩu mới'
            size='large'
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
          />
        </Form.Item>

        <Form.Item
          label='Xác nhận mật khẩu'
          name='confirmPassword'
          rules={[
            {
              required: true,
              message: 'Vui lòng xác nhận mật khẩu',
            },
          ]}
        >
          <Input
            prefix={<LockOutlined className='text-gray-400' />}
            suffix={
              <button
                type='button'
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className='cursor-pointer text-gray-400 hover:text-gray-600 border-0 bg-transparent p-0'
                aria-label={showConfirmPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
              >
                {showConfirmPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </button>
            }
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder='Nhập lại mật khẩu mới'
            size='large'
            value={confirmPassword}
            onChange={(e) => onConfirmPasswordChange(e.target.value)}
          />
        </Form.Item>

        {password && (
          <div className='mb-3 p-3 bg-gray-50 rounded-lg'>
            <p className='text-xs font-semibold mb-2 text-gray-700'>Mật khẩu phải bao gồm:</p>
            <ValidationItem isValid={validation.minLength} text='Tối thiểu 9 ký tự' />
            <ValidationItem isValid={validation.hasNumber} text='Ít nhất 1 chữ số (0-9)' />
            <ValidationItem isValid={validation.hasLowerCase} text='Ít nhất 1 chữ thường (a-z)' />
            <ValidationItem isValid={validation.hasUpperCase} text='Ít nhất 1 chữ in hoa (A-Z)' />
            <ValidationItem
              isValid={validation.hasSpecialChar}
              text='Ít nhất 1 ký tự đặc biệt (!@#$...)'
            />
            {confirmPassword && (
              <ValidationItem isValid={validation.isMatch} text='Mật khẩu khớp nhau' />
            )}
          </div>
        )}

        <Form.Item className='mb-4'>
          <Button
            type='primary'
            htmlType='submit'
            size='large'
            loading={loading}
            disabled={!isValid}
            className='w-full bg-[#1677ff]'
          >
            Đặt lại mật khẩu
          </Button>
        </Form.Item>

        <Form.Item className='mb-0'>
          <Button type='link' onClick={onBack} className='w-full text-gray-600' disabled={loading}>
            Quay lại
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};
